import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FaBook } from "react-icons/fa";
import { obtenerManual } from "../data/rutaManuales";

const parseMarkdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  
  // Headers (H3, H4, H5, H6)
  html = html.replace(/^#### (.*?)$/gm, '<h6 class="mt-3 mb-2 fw-bold text-dark">$1</h6>');
  html = html.replace(/^### (.*?)$/gm, '<h5 class="mt-4 mb-2 fw-bold text-dark border-bottom pb-1">$1</h5>');
  html = html.replace(/^## (.*?)$/gm, '<h4 class="mt-4 mb-3 fw-bold text-dark">$1</h4>');
  html = html.replace(/^# (.*?)$/gm, '<h3 class="mt-4 mb-3 fw-bold text-dark">$1</h3>');
  
  // Bold (**text**)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Italics (*text*)
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Links ([text](url))
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-primary fw-semibold">$1</a>');
  
  // Code block (```code```)
  html = html.replace(/```([\s\S]*?)```/g, '<pre class="bg-light p-3 border rounded font-monospace my-3" style="font-size: 13px; white-space: pre-wrap; max-height: 350px; overflow: auto; color: #212529;">$1</pre>');
  
  // Inline code (`code`)
  html = html.replace(/`(.*?)`/g, '<code class="bg-light px-1 border rounded font-monospace" style="color: #d63384; font-size: 85%; font-weight: 500;">$1</code>');
  
  // Blockquotes / Alerts
  html = html.replace(/^\s*&gt;\s+\[!IMPORTANT\]\s*(.*?)$/gm, '<div class="alert alert-danger py-2 px-3 my-2 small d-flex align-items-center gap-2"><strong>IMPORTANTE:</strong> $1</div>');
  html = html.replace(/^\s*&gt;\s+\[!WARNING\]\s*(.*?)$/gm, '<div class="alert alert-warning py-2 px-3 my-2 small d-flex align-items-center gap-2"><strong>ADVERTENCIA:</strong> $1</div>');
  html = html.replace(/^\s*&gt;\s+\[!NOTE\]\s*(.*?)$/gm, '<div class="alert alert-info py-2 px-3 my-2 small d-flex align-items-center gap-2"><strong>NOTA:</strong> $1</div>');
  html = html.replace(/^\s*&gt;\s+(.*?)$/gm, '<blockquote class="border-start border-3 ps-3 text-muted" style="font-style: italic; border-color: #6c757d !important;">$1</blockquote>');

  // List items
  html = html.replace(/^\s*-\s+(.*?)$/gm, '<li>$1</li>');
  html = html.replace(/^\s*\d+\.\s+(.*?)$/gm, '<li>$1</li>');

  // Table parser
  const parseTableBlock = (rows) => {
    if (!rows || rows.length < 2) return rows.join('\n');
    const isSeparator = (row) => /^\s*\|([\s\-:|]+\|)+\s*$/.test(row);
    let headerRows = [rows[0]];
    let bodyRows = rows.slice(1).filter(r => !isSeparator(r));

    const parseCells = (row) => row
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(c => c.trim());

    const headers = parseCells(headerRows[0]);
    let tableHtml = '<div class="table-responsive my-3"><table class="table table-bordered table-striped table-sm">';
    tableHtml += '<thead class="table-light"><tr>' + headers.map(h => `<th>${h}</th>`).join('') + '</tr></thead>';
    if (bodyRows.length) {
      tableHtml += '<tbody>' + bodyRows.map(row => {
        const cells = parseCells(row);
        return '<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>';
      }).join('') + '</tbody>';
    }
    tableHtml += '</table></div>';
    return tableHtml;
  };

  // Handle tables, lists and paragraphs
  const lines = html.split('\n');
  let inList = false;
  let inTable = false;
  let tableRows = [];
  let result = [];

  const flushTable = () => {
    if (tableRows.length) {
      result.push(parseTableBlock(tableRows));
      tableRows = [];
      inTable = false;
    }
  };

  for (let line of lines) {
    const trimmed = line.trim();
    const isTableRow = /^\s*\|.*\|\s*$/.test(trimmed);

    if (isTableRow) {
      if (!inTable) {
        inTable = true;
      }
      tableRows.push(line);
      continue;
    } else {
      flushTable();
    }

    if (trimmed.startsWith('<li>')) {
      if (!inList) {
        result.push('<ul class="my-2" style="padding-left: 20px;">');
        inList = true;
      }
      result.push(line);
    } else {
      if (inList) {
        result.push('</ul>');
        inList = false;
      }

      if (trimmed.startsWith('<h') || trimmed.startsWith('<pre') || trimmed.startsWith('</pre') ||
          trimmed.startsWith('<div') || trimmed.startsWith('</div') || trimmed.startsWith('<blockquote') ||
          trimmed.startsWith('</blockquote') || trimmed === '') {
        result.push(line);
      } else {
        result.push(`<p class="mb-2 text-secondary" style="line-height: 1.6;">${line}</p>`);
      }
    }
  }

  flushTable();
  if (inList) {
    result.push('</ul>');
  }

  return result.join('\n');
};

export default function BotonManualHeader({ usuario }) {
  const location = useLocation();
  const navigate = useNavigate();
  const manual = obtenerManual(location.pathname);
  const [showManual, setShowManual] = useState(false);
  const [content, setContent] = useState("");
  const [loadingManual, setLoadingManual] = useState(false);

  if (!manual) return null;

  const handleOpenManual = async () => {
    setLoadingManual(true);
    setShowManual(true);
    const origin = window.location.origin;
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    const path = `${origin}${base}/manuales/${manual.modulo}/${manual.archivo.replace(/\.(js|jsx)$/i, "")}.md`;
    console.log("Intentando cargar manual desde ruta:", path);
    try {
      const response = await fetch(path);
      if (response.ok) {
        const text = await response.text();
        setContent(text);
      } else {
        console.warn(`Fetch manual falló con status: ${response.status} para ruta: ${path}`);
        setContent("> Manual no disponible para esta pantalla.");
      }
    } catch (err) {
      console.error("Excepción al hacer fetch del manual:", err);
      setContent("> Error al cargar el manual.");
    }
    setLoadingManual(false);
  };

  return (
    <>
      <Button
        variant="outline-light"
        onClick={handleOpenManual}
        style={{
          borderRadius: "8px",
          fontWeight: "500",
          fontSize: "13px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 12px",
          borderColor: "rgba(255,255,255,0.4)"
        }}
      >
        <FaBook /> Ver Manual
      </Button>

      <Modal
        show={showManual}
        onHide={() => setShowManual(false)}
        size="lg"
        scrollable
        centered
      >
        <Modal.Header closeButton style={{ borderBottom: "2px solid #f39c12" }}>
          <Modal.Title style={{ fontWeight: 600 }}>
            <FaBook className="me-2 text-success" />
            Manual: {manual.nombre}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#f8f9fa", padding: "24px" }}>
          {loadingManual ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-2 text-muted">Cargando manual de usuario...</p>
            </div>
          ) : (
            <div 
              className="manual-content bg-white p-4 rounded shadow-sm border" 
              onClick={(e) => {
                const target = e.target.closest("a");
                if (target) {
                  const href = target.getAttribute("href");
                  if (href && href.startsWith("/")) {
                    e.preventDefault();
                    setShowManual(false);
                    navigate(href);
                  }
                }
              }}
              dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(content) }} 
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManual(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
